import React from 'react';
import { useNavigate } from "react-router-dom";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const SendMoneyModal = ({ open, handleClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();

  const handleLocalClick = () => {
    handleClose(); // close modal first (optional but recommended)
    navigate("/local-transfer"); // navigate to local transfer route
  };

  const handleInternationalClick = () => {
    handleClose();
    navigate("/deposits"); // navigate to deposits (international) route
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 3,
          width: isMobile ? 'calc(100% - 20px)' : 360,
          maxHeight: '92vh',
          overflow: 'visible',
        }}
      >
        {/* Close Icon */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Top Icon */}
        <Box
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#1d4ed8' : '#7dd3fc',
            width: 60,
            height: 60,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <SendIcon sx={{ color: 'white', fontSize: 30 }} />
        </Box>

        {/* Titles */}
        <Typography variant="h6" align="center" gutterBottom>
          Send Money
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" mb={3}>
          Swift and Secure Money Transfer
        </Typography>

        {/* Local Transfer Button */}
        <Box
          component="button"
          onClick={handleLocalClick}
          sx={{
            bgcolor: theme.palette.action.hover,
            borderRadius: 2,
            p: 2,
            mb: 2,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            border: 'none',
            outline: 'none',
            transition: 'transform 0.2s ease, background-color 0.2s ease',
            '&:hover': {
              transform: 'scale(1.02)',
              backgroundColor: theme.palette.action.selected,
            },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonIcon color="primary" />
            <Box sx={{ textAlign: 'left' }}>
              <Typography fontWeight="bold">Local Transfer</Typography>
              <Typography variant="caption">Easily send money locally</Typography>
              <Typography variant="caption" display="block">
                0% Handling charges
              </Typography>
            </Box>
          </Stack>
          <ArrowForwardIosIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        </Box>

        {/* International Transfer Button */}
        <Box
          component="button"
          onClick={handleInternationalClick}
          sx={{
            bgcolor: theme.palette.action.hover,
            borderRadius: 2,
            p: 2,
            mb: 3,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            border: 'none',
            outline: 'none',
            transition: 'transform 0.2s ease, background-color 0.2s ease',
            '&:hover': {
              transform: 'scale(1.02)',
              backgroundColor: theme.palette.action.selected,
            },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <PublicIcon color="primary" />
            <Box sx={{ textAlign: 'left' }}>
              <Typography fontWeight="bold">International Wire Transfer</Typography>
              <Typography variant="caption">
                Wire transfer is executed under 72 hours
              </Typography>
              <Typography variant="caption" display="block">
                IBAN & SWIFT code required
              </Typography>
            </Box>
          </Stack>
          <ArrowForwardIosIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        </Box>

        {/* Bottom Close Button */}
        <Box sx={{ px: 1, mt: 2, mb: 5 }}>
          <Button fullWidth onClick={handleClose} variant="outlined">
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SendMoneyModal;